import { useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { X, Search } from "lucide-react";
import ClassificationDetails from "./ClassificationDetails";
import { useSelector } from "react-redux";
import {
  postClassificationForApplication,
  getNaicsCmiIsoDetails
} from "../../api/classificationService";

const ClassificationModal = ({ open, onClose }) => {
  const accessToken = localStorage.getItem("access_token") || "";
  const refreshToken = localStorage.getItem("refresh_token") || "";
  const selectedApplicationDetails = useSelector(
    (state) => state.intakeData?.selectedApplicationData
  );
  const [type, setType] = useState("CMI");
  const [code, setCode] = useState("");
  const [filledData, setFilledData] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const handleAddClassification = async () => {
    if (!code || !type || !filledData) return;
    setAddLoading(true);
    try {
      const res = await postClassificationForApplication({
        applicationId: selectedApplicationDetails.application_id,
        customerId: selectedApplicationDetails.customer_id,
        cmiList: [code],
        accessToken,
        refreshToken
      });

      setResponseMessage(res.message || "Classification successfully added."); // ✅ Show message
      // You can close the modal later instead of immediately
      // onClose();
    } catch (error) {
      console.error("Error adding classification:", error);
      setResponseMessage("Something went wrong while adding classification."); // ✅ Show error
    } finally {
      setAddLoading(false);
    }
  };

  const handleSearchClassification = async () => {
    if (!code || !type) return;
    setLoading(true);
    try {
      const response = await getNaicsCmiIsoDetails(
        {
          search_type: type === "ISO-GL" ? "iso" : type.toLowerCase(),
          search_value: code
        },
        accessToken,
        refreshToken
      );

      setFilledData(response[0]);
    } catch (error) {
      console.error("Search failed:", error);
      setFilledData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const input = e.target.value.trim();
    setCode(input);
  };

  return (
    <Dialog open={open} onOpenChange={onClose} className="z-12">
      <DialogContent className="max-w-[700px] w-[600px] space-y-2 bg-white text-left">
        <div className="space-y-1">
          <h2 className="text-sm text-gray-500">
            New Classification &gt; {type} Filled
          </h2>
          <h3 className="text-lg font-semibold text-black flex items-center gap-2">
            <span className="text-red-500 text-xl">+</span> Add New
            Classification
          </h3>
          <p className="text-sm text-gray-500">
            Add a classification to enhance the decision matrix
          </p>
        </div>
        <button
          className="absolute right-4 text-gray-500 hover:text-gray-700"
          onClick={onClose}
        >
          <X size={18} />
        </button>
        <div>
          <div>
            <p className="text-sm font-medium mb-2">Classification Type</p>
            <div
              className="flex gap-3"
              role="radiogroup"
              aria-label="Classification Type"
            >
              {["CMI", "ISO-GL", "NAICS"].map((val) => {
                const isSelected = type === val;
                return (
                  <label
                    key={val}
                    className={`flex items-center gap-2 px-2 py-3 rounded-lg border text-sm font-semibold min-w-[80px] justify-center cursor-pointer transition ${
                      isSelected
                        ? "bg-[#FFF0ED] border-[#FF5533] text-black"
                        : "bg-white border-[#E0E0E0] text-black hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="classification-type"
                      value={val}
                      checked={isSelected}
                      onChange={() => {
                        setType(val);
                        setCode("");
                        setFilledData(null);
                      }}
                      className="sr-only"
                    />
                    <span
                      className={`w-4 h-4 rounded-full border-4 flex items-center justify-center ${
                        isSelected ? "border-[#FF5533]" : "border-[#D0D5DD]"
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isSelected ? "bg-white" : "bg-transparent"
                        }`}
                      />
                    </span>
                    <span className="text-base font-semibold">{val}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <label className="text-sm font-medium mt-4 block">{type} Code</label>
          <div className="relative my-2">
            <input
              className="w-full border border-gray-300 px-3 py-2 text-sm rounded outline-none focus:ring-1 focus:ring-orange-300 pr-10"
              placeholder={`e.g. ${
                type === "CMI" ? "2103" : type === "ISO-GL" ? "44444" : "115112"
              }`}
              value={code}
              onChange={handleChange}
            />
            <button
              onClick={handleSearchClassification}
              aria-label="Search classification"
              type="button"
              disabled={loading}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center"
            >
              {loading ? (
                <span
                  className="animate-spin w-4 h-4 border-2 border-t-transparent rounded-full"
                  style={{
                    borderColor: "#f97316", // orange-500
                    borderTopColor: "transparent"
                  }}
                />
              ) : (
                <Search size={18} color="#f97316" />
              )}
            </button>
          </div>

          {filledData && (
            <ClassificationDetails data={filledData} type={type} />
          )}

          {responseMessage && (
            <p className="text-sm text-green-600 mt-2">{responseMessage}</p>
          )}

          <div className="flex justify-end gap-2 mt-10">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border rounded bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddClassification}
              disabled={addLoading}
              className="bg-[#FF5533] hover:bg-[#e74c2c] text-white px-4 py-2 rounded flex items-center gap-1"
            >
              {addLoading ? (
                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>Add Classification</>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClassificationModal;
